/**
 * Teen Patti (3 Patti) — server-authoritative session engine.
 *
 * Rules implemented (configurable via {@link TeenPattiConfig}):
 *  - 2..maxPlayers players, one 52-card deck, 3 cards each. Every player posts the boot (ante).
 *  - Current stake S starts at the boot. A BLIND player bets S (chaal) or 2S (raise → S doubles);
 *    a SEEN player bets 2S or 4S (raise → S doubles). The stake may not exceed `chaalLimit`.
 *  - A blind player may look at their cards at any time ("see"), even off-turn. After `maxBlinds`
 *    blind bets a player must see before acting again.
 *  - Pack (fold) at any time on your turn.
 *  - SHOW: only when exactly two players remain; costs the requester's current bet (blind S, seen 2S).
 *    Hands are compared; on a tie the player who asked for the show loses.
 *  - SIDE SHOW (`sideShow` toggle): a seen player, with 3+ players active, pays a chaal (2S) and asks the
 *    previous active seen player to compare privately. If accepted, the weaker hand packs (tie → requester
 *    packs) and only the two players see each other's cards. If declined, play continues.
 *  - Pot limit: when the pot reaches `potLimit` all remaining hands are shown automatically; best hand wins,
 *    exact ties split the pot.
 *  - Insufficient chips: a player who cannot cover the minimum bet may only pack, or — heads-up — show for
 *    all their remaining chips.
 *  - Ranking: Trail > Pure sequence > Sequence > Colour > Pair > High card; A-K-Q is the top sequence,
 *    A-2-3 ranks second (`ace23: 'second'`) or lowest (`'low'`). Suits never break ties.
 *  - `variant: 'muflis'` inverts the ranking (lowest hand wins).
 *  - Optional rake (coin sink) taken from each pot.
 *  - Turn timeout → pack. Side-show request timeout → declined.
 */
import { buildDeck, sortHand, type CardId } from '../../core/cards.js';
import { checkBool, checkEnum, checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import { nextSeatWhere, prevSeatWhere, seatsWhere, splitAmount } from '../../core/seats.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';
import { compareTeenPatti, evaluateTeenPatti, type TeenPattiCategory } from './hands.js';

export interface TeenPattiConfig {
  maxPlayers: number;
  minPlayers: number;
  boot: number;
  maxBlinds: number;
  /** Maximum stake S (in chips). */
  chaalLimit: number;
  potLimit: number;
  sideShow: boolean;
  ace23: 'second' | 'low';
  /** Flush: a colour beats a sequence (used by the Flush game). */
  flushRanking: boolean;
  /** Three Card Brag: 3-3-3 tops the prials and A-2-3 tops the runs (used by the Brag game). */
  bragRanking: boolean;
  variant: 'classic' | 'muflis';
  rakePercent: number;
  handEndDelayMs: number;
  startDelayMs: number;
}

export type TPStatus = 'WAITING' | 'BLIND' | 'SEEN' | 'PACKED';

export interface TPPlayer {
  playerId: string;
  chips: number;
  status: TPStatus;
  cards: CardId[];
  blindsPlayed: number;
  /** Total chips put into the current pot. */
  invested: number;
}

export type TPPhase = 'WAITING' | 'BETTING' | 'SIDESHOW' | 'HAND_END';

export interface TPHandResult {
  winners: number[];
  pot: number;
  rake: number;
  payouts: Record<number, number>;
  /** Hands shown publicly at the end (showdown). Empty when everybody else packed. */
  revealed: Record<number, CardId[]>;
  categories: Record<number, TeenPattiCategory>;
  reason: 'ALL_PACKED' | 'SHOW' | 'POT_LIMIT';
}

export interface TeenPattiState {
  config: TeenPattiConfig;
  seats: (TPPlayer | null)[];
  phase: TPPhase;
  handNo: number;
  dealer: number;
  turn: number;
  stake: number;
  pot: number;
  sideShow: { from: number; to: number } | null;
  /** Private side-show reveals: viewer seat → (seat → cards). */
  privateReveals: Record<number, Record<number, CardId[]>>;
  lastResult: TPHandResult | null;
  rakeTotal: number;
  lastAction: { seat: number; action: string; amount: number } | null;
}

export type TeenPattiAction =
  | { type: 'see' }
  | { type: 'bet'; raise: boolean }
  | { type: 'pack' }
  | { type: 'show' }
  | { type: 'sideshow' }
  | { type: 'sideshowRespond'; accept: boolean };

export interface TeenPattiLegal {
  canSee: boolean;
  canPack: boolean;
  /** Chips required for a call (chaal) / raise; null when not allowed. */
  call: number | null;
  raise: number | null;
  show: number | null;
  sideShow: number | null;
  respondSideShow: boolean;
}

export interface TeenPattiPlayerView {
  seat: number;
  playerId: string;
  chips: number;
  status: TPStatus;
  invested: number;
  blindsPlayed: number;
  hasCards: boolean;
}

export interface TeenPattiView {
  gameType: 'TEEN_PATTI';
  config: TeenPattiConfig;
  seat: number | null;
  phase: TPPhase;
  handNo: number;
  dealer: number;
  turn: number;
  stake: number;
  pot: number;
  players: (TeenPattiPlayerView | null)[];
  /** Own cards — only once the player has seen them. */
  hand: CardId[];
  sideShow: { from: number; to: number } | null;
  sideShowReveal: Record<number, CardId[]> | null;
  lastResult: TPHandResult | null;
  lastAction: TeenPattiState['lastAction'];
}

const DEFAULTS: TeenPattiConfig = {
  maxPlayers: 5,
  minPlayers: 2,
  boot: 100,
  maxBlinds: 4,
  chaalLimit: 12800,
  potLimit: 102400,
  sideShow: true,
  ace23: 'second',
  flushRanking: false,
  bragRanking: false,
  variant: 'classic',
  rakePercent: 0,
  handEndDelayMs: 5000,
  startDelayMs: 2500,
};

export const rankOpts = (c: Pick<TeenPattiConfig, 'ace23' | 'flushRanking' | 'bragRanking'>) => ({ ace23: c.ace23, colorFirst: c.flushRanking, brag: c.bragRanking });

const inHand = (p: TPPlayer) => p.status === 'BLIND' || p.status === 'SEEN';

function betCost(p: TPPlayer, stake: number, raise: boolean): number {
  const base = p.status === 'SEEN' ? stake * 2 : stake;
  return raise ? base * 2 : base;
}

function activeSeats(s: TeenPattiState): number[] {
  return seatsWhere(s.seats, inHand);
}

function compare(s: TeenPattiState, a: number, b: number): number {
  return compareTeenPatti(s.seats[a]!.cards, s.seats[b]!.cards, rankOpts(s.config), s.config.variant === 'muflis');
}

function settle(s: TeenPattiState, winners: number[], reason: TPHandResult['reason'], revealSeats: number[], events: EngineEvent[]): void {
  const rake = Math.floor((s.pot * s.config.rakePercent) / 100);
  const net = s.pot - rake;
  const shares = splitAmount(net, winners);
  const payouts: Record<number, number> = {};
  for (const [seat, amt] of shares) {
    s.seats[seat]!.chips += amt;
    payouts[seat] = amt;
  }
  const revealed: Record<number, CardId[]> = {};
  const categories: Record<number, TeenPattiCategory> = {};
  for (const seat of revealSeats) {
    revealed[seat] = [...s.seats[seat]!.cards];
    categories[seat] = evaluateTeenPatti(s.seats[seat]!.cards, rankOpts(s.config)).category;
  }
  s.rakeTotal += rake;
  s.lastResult = { winners, pot: s.pot, rake, payouts, revealed, categories, reason };
  events.push({ type: 'handEnd', ...s.lastResult });
  s.pot = 0;
  s.sideShow = null;
  s.phase = 'HAND_END';
}

function endIfOneLeft(s: TeenPattiState, events: EngineEvent[]): boolean {
  const active = activeSeats(s);
  if (active.length === 1) {
    settle(s, active, 'ALL_PACKED', [], events);
    return true;
  }
  return false;
}

function showdownAll(s: TeenPattiState, reason: TPHandResult['reason'], events: EngineEvent[]): void {
  const active = activeSeats(s);
  let best: number[] = [];
  for (const seat of active) {
    if (best.length === 0) best = [seat];
    else {
      const c = compare(s, seat, best[0]!);
      if (c > 0) best = [seat];
      else if (c === 0) best.push(seat);
    }
  }
  settle(s, best, reason, active, events);
}

function advanceTurn(s: TeenPattiState, from: number): void {
  const next = nextSeatWhere(s.seats, from, inHand);
  if (next !== null) s.turn = next;
}

function pay(s: TeenPattiState, seat: number, amount: number): void {
  const p = s.seats[seat]!;
  p.chips -= amount;
  p.invested += amount;
  s.pot += amount;
}

function canStart(s: TeenPattiState): boolean {
  return seatsWhere(s.seats, (p) => p.chips >= s.config.boot).length >= s.config.minPlayers;
}

function startHand(s: TeenPattiState, rng: Rng, events: EngineEvent[]): void {
  const cfg = s.config;
  const deck = shuffle(buildDeck(), rng);
  s.handNo++;
  s.privateReveals = {};
  s.lastResult = null;
  s.lastAction = null;
  s.sideShow = null;
  s.stake = cfg.boot;
  s.pot = 0;
  const participants = seatsWhere(s.seats, (p) => p.chips >= cfg.boot);
  for (const seat of seatsWhere(s.seats, () => true)) {
    const p = s.seats[seat]!;
    p.cards = [];
    p.invested = 0;
    p.blindsPlayed = 0;
    p.status = participants.includes(seat) ? 'BLIND' : 'WAITING';
  }
  s.dealer = nextSeatWhere(s.seats, s.dealer, inHand) ?? participants[0]!;
  for (const seat of participants) {
    pay(s, seat, cfg.boot);
    s.seats[seat]!.cards = sortHand(deck.splice(0, 3));
  }
  s.phase = 'BETTING';
  s.turn = nextSeatWhere(s.seats, s.dealer, inHand)!;
  events.push({ type: 'handStart', handNo: s.handNo, dealer: s.dealer, participants, boot: cfg.boot, pot: s.pot });
}

function legalFor(s: TeenPattiState, seat: number): TeenPattiLegal {
  const none: TeenPattiLegal = { canSee: false, canPack: false, call: null, raise: null, show: null, sideShow: null, respondSideShow: false };
  const p = s.seats[seat];
  if (!p || !inHand(p)) return none;
  if (s.phase === 'SIDESHOW') return { ...none, canSee: p.status === 'BLIND', respondSideShow: s.sideShow?.to === seat };
  if (s.phase !== 'BETTING') return none;
  const legal: TeenPattiLegal = { ...none, canSee: p.status === 'BLIND' };
  if (s.turn !== seat) return legal;
  legal.canPack = true;
  const mustSee = p.status === 'BLIND' && p.blindsPlayed >= s.config.maxBlinds;
  const active = activeSeats(s);
  const callCost = betCost(p, s.stake, false);
  if (!mustSee && p.chips >= callCost) {
    legal.call = callCost;
    const raiseCost = betCost(p, s.stake, true);
    if (s.stake * 2 <= s.config.chaalLimit && p.chips >= raiseCost) legal.raise = raiseCost;
  }
  if (active.length === 2 && !mustSee) legal.show = Math.min(callCost, p.chips);
  if (s.config.sideShow && active.length >= 3 && p.status === 'SEEN' && p.chips >= callCost) {
    const prev = prevSeatWhere(s.seats, seat, inHand);
    if (prev !== null && s.seats[prev]!.status === 'SEEN') legal.sideShow = callCost;
  }
  return legal;
}

function afterBet(s: TeenPattiState, seat: number, events: EngineEvent[]): void {
  if (s.pot >= s.config.potLimit) {
    events.push({ type: 'potLimitReached', pot: s.pot });
    showdownAll(s, 'POT_LIMIT', events);
    return;
  }
  advanceTurn(s, seat);
}

function packSeat(s: TeenPattiState, seat: number, events: EngineEvent[]): void {
  s.seats[seat]!.status = 'PACKED';
  events.push({ type: 'packed', seat });
}

export const teenPattiEngine: GameEngine<TeenPattiConfig, TeenPattiState, TeenPattiAction, TeenPattiView, TeenPattiLegal> = {
  gameType: 'TEEN_PATTI',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxPlayers', c.maxPlayers, 2, 7);
    checkInt('minPlayers', c.minPlayers, 2, c.maxPlayers);
    checkInt('boot', c.boot, 1, 1e9);
    checkInt('maxBlinds', c.maxBlinds, 0, 20);
    checkInt('chaalLimit', c.chaalLimit, c.boot, 1e12);
    checkInt('potLimit', c.potLimit, c.boot * 2, 1e13);
    checkBool('sideShow', c.sideShow);
    checkBool('flushRanking', c.flushRanking);
    checkBool('bragRanking', c.bragRanking);
    checkEnum('ace23', c.ace23, ['second', 'low'] as const);
    checkEnum('variant', c.variant, ['classic', 'muflis'] as const);
    checkInt('rakePercent', c.rakePercent, 0, 20);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: TeenPattiState = {
      config,
      seats: Array<TPPlayer | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      dealer: rng.int(config.maxPlayers),
      turn: 0,
      stake: config.boot,
      pot: 0,
      sideShow: null,
      privateReveals: {},
      lastResult: null,
      rakeTotal: 0,
      lastAction: null,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: 'WAITING', cards: [], blindsPlayed: 0, invested: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    const events: EngineEvent[] = [];
    const wasActive = inHand(p) && (s.phase === 'BETTING' || s.phase === 'SIDESHOW');
    if (wasActive) {
      if (s.phase === 'SIDESHOW' && s.sideShow && (s.sideShow.from === seat || s.sideShow.to === seat)) {
        const from = s.sideShow.from;
        s.sideShow = null;
        s.phase = 'BETTING';
        s.turn = from;
      }
      const wasTurn = s.turn === seat;
      packSeat(s, seat, events);
      if (!endIfOneLeft(s, events) && wasTurn) advanceTurn(s, seat);
    }
    s.seats[seat] = null;
    events.push({ type: 'playerLeft', seat });
    return { chips: p.chips, events };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    switch (raw.type) {
      case 'see':
      case 'pack':
      case 'show':
      case 'sideshow':
        return { type: raw.type };
      case 'bet':
        return { type: 'bet', raise: raw.raise === true };
      case 'sideshowRespond':
        return { type: 'sideshowRespond', accept: raw.accept === true };
      default:
        throw new EngineError('INVALID_ACTION', 'Unknown Teen Patti action');
    }
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p && inHand(p), 'INVALID_ACTION', 'You are not in this hand');
    assertEngine(s.phase === 'BETTING' || s.phase === 'SIDESHOW', 'INVALID_PHASE', 'No hand in progress');
    const events: EngineEvent[] = [];
    const legal = legalFor(s, seat);

    if (action.type === 'see') {
      assertEngine(legal.canSee, 'INVALID_ACTION', 'Cards already seen');
      p.status = 'SEEN';
      events.push({ type: 'seen', seat });
      return events;
    }

    if (action.type === 'sideshowRespond') {
      assertEngine(legal.respondSideShow && s.sideShow, 'NOT_YOUR_TURN', 'No side show to answer');
      const { from, to } = s.sideShow;
      s.sideShow = null;
      s.phase = 'BETTING';
      if (!action.accept) {
        events.push({ type: 'sideShowDeclined', from, to });
      } else {
        const c = compare(s, from, to);
        const loser = c > 0 ? to : from;
        s.privateReveals[from] = { ...(s.privateReveals[from] ?? {}), [to]: [...s.seats[to]!.cards] };
        s.privateReveals[to] = { ...(s.privateReveals[to] ?? {}), [from]: [...s.seats[from]!.cards] };
        events.push({ type: 'sideShowResult', from, to, loser });
        packSeat(s, loser, events);
        if (endIfOneLeft(s, events)) return events;
      }
      advanceTurn(s, from);
      return events;
    }

    assertEngine(s.phase === 'BETTING' && s.turn === seat, 'NOT_YOUR_TURN', 'Not your turn');
    s.lastAction = null;
    switch (action.type) {
      case 'pack': {
        packSeat(s, seat, events);
        s.lastAction = { seat, action: 'pack', amount: 0 };
        if (!endIfOneLeft(s, events)) advanceTurn(s, seat);
        return events;
      }
      case 'bet': {
        const cost = action.raise ? legal.raise : legal.call;
        assertEngine(cost !== null, action.raise ? 'INVALID_AMOUNT' : 'INSUFFICIENT_CHIPS', action.raise ? 'Raise not allowed' : 'Cannot call');
        pay(s, seat, cost);
        if (action.raise) s.stake *= 2;
        if (p.status === 'BLIND') p.blindsPlayed++;
        s.lastAction = { seat, action: action.raise ? 'raise' : 'chaal', amount: cost };
        events.push({ type: action.raise ? 'raise' : 'chaal', seat, amount: cost, stake: s.stake, pot: s.pot, blind: p.status === 'BLIND' });
        afterBet(s, seat, events);
        return events;
      }
      case 'show': {
        assertEngine(legal.show !== null, 'ILLEGAL_MOVE', 'Show needs exactly two players');
        pay(s, seat, legal.show);
        const other = activeSeats(s).find((x) => x !== seat)!;
        const c = compare(s, seat, other);
        const winner = c > 0 ? seat : other; // tie → requester loses
        s.lastAction = { seat, action: 'show', amount: legal.show };
        events.push({ type: 'show', seat, against: other, amount: legal.show });
        settle(s, [winner], 'SHOW', [seat, other], events);
        return events;
      }
      case 'sideshow': {
        assertEngine(legal.sideShow !== null, 'ILLEGAL_MOVE', 'Side show not available');
        const to = prevSeatWhere(s.seats, seat, inHand)!;
        pay(s, seat, legal.sideShow);
        s.sideShow = { from: seat, to };
        s.phase = 'SIDESHOW';
        s.lastAction = { seat, action: 'sideshow', amount: legal.sideShow };
        events.push({ type: 'sideShowRequested', from: seat, to, amount: legal.sideShow, pot: s.pot });
        if (s.pot >= s.config.potLimit) {
          s.sideShow = null;
          s.phase = 'BETTING';
          events.push({ type: 'potLimitReached', pot: s.pot });
          showdownAll(s, 'POT_LIMIT', events);
        }
        return events;
      }
    }
  },

  getActors(s) {
    if (s.phase === 'BETTING') return [s.turn];
    if (s.phase === 'SIDESHOW' && s.sideShow) return [s.sideShow.to];
    return [];
  },

  getLegalActions: (s, seat) => legalFor(s, seat),

  getTimeoutAction(s, seat) {
    if (s.phase === 'SIDESHOW' && s.sideShow?.to === seat) return { type: 'sideshowRespond', accept: false };
    return { type: 'pack' };
  },

  getView(s, seat) {
    const me = seat === null ? null : s.seats[seat];
    return {
      gameType: 'TEEN_PATTI',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      dealer: s.dealer,
      turn: s.turn,
      stake: s.stake,
      pot: s.pot,
      players: s.seats.map((p, i) =>
        p
          ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, invested: p.invested, blindsPlayed: p.blindsPlayed, hasCards: p.cards.length > 0 }
          : null,
      ),
      hand: me && (me.status === 'SEEN' || (s.phase === 'HAND_END' && me.cards.length > 0)) ? [...me.cards] : [],
      sideShow: s.sideShow,
      sideShowReveal: seat !== null && s.privateReveals[seat] ? structuredClone(s.privateReveals[seat]) : null,
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
      lastAction: s.lastAction,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      for (const p of s.seats) if (p) p.status = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'BETTING' || s.phase === 'SIDESHOW',
  getChips: (s) => Object.fromEntries(seatsWhere(s.seats, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s.seats, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => (s.phase === 'HAND_END' ? s.handNo : Math.max(0, s.handNo - 1)),
};
